import { Badge, Col, Form, Container as Grid, InputGroup, Nav, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import SummaryCard from "components/core/blocks/SummaryCard";
import { useMemo, useRef } from "react";
import { useFetch } from "hooks/api";
import { useDates } from "dates/DatesContext";
import { DatesProvider } from "dates/DatesContext";
import Table from "views/merchandise/tables/BulkActionsTable";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import KeywordCell from "components/custom/category/advertisements/KeywordCell";
import { useAppSelector } from "redux/hooks";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "utils/dashboardLinks";
import { useAcosOrRoas } from "utils/acosOrRoas";
import useDebounce from "hooks/useDebounce";
import useQueryParams from "hooks/useQueryParams";
import moment from "moment";
import { useModuleEnabled } from "redux/selectors/modules";
import useSearchTermReport from "hooks/useSearchTermReport";
import CategorySelector from "components/core/blocks/CategorySelector";
import DownloadReport from "modules/perf_reports/DownloadReport";
import { SEARCH_TERM_REPORT_MATCH_TYPE_FILTER } from "./advertising/targetLifecycleFilters";
import { formatCurrency } from "utils/formatNumber";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import QueryExpander from "modules/query_funnel/QueryExpander";

export const useAdvertisementReport = (params = {}) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const start = moment().add(30, "days").format("YYYY-MM-DD");
  const end = start;

  return useFetch(
    ["advertisementReport", start, end, marketPlace.marketPlace],
    `/api/data_report/ad_report`,
    {
      start_date: start,
      end_date: end,
      channel: channel.currentChannel,
      mp: marketPlace.marketPlace,
      ...params,
    },
    {
      default: [],
      staleTime: 1000 * 60 * 5,
      select: (res) => res.data,
    },
  );
};

export const useTargetReport = (targetIds, targetType, start, end, pre_start, pre_end, group) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  return useFetch(
    ["targetReport", targetIds, targetType, start, end, pre_start, pre_end, group],
    `/api/data_report/keyword_report`,
    {
      target_ids: targetIds.join(","),
      start_date: start,
      end_date: end,
      pre_start_date: pre_start,
      pre_end_date: pre_end,
      empty: true,
      channel: channel.currentChannel,
      kw_report: false,
      target_type: targetType,
      group,
      mp: marketPlace.marketPlace,
    },
    {
      default: [],
      enabled: !!start && !!end && !!targetIds.length,
      staleTime: 1000 * 60 * 5,
      select: (res) => res.data,
    },
  );
};

const formatScheduledActionLabel = (scheduledAction, marketPlace) => {
  const field = scheduledAction?.scheduled_action_fields?.[0];
  const startDate = moment(scheduledAction?.scheduled_action_start_date).utc().format("MMM D");
  const endDate = moment(scheduledAction?.scheduled_action_end_date).utc().format("MMM D");

  if (!field) return "";

  const { field_name, change_value } = field;

  if (field_name === "timebound_per_change") {
    const percentChange = Math.round(change_value * 100) - 100;
    const sign = percentChange > 0 ? "+" : "";

    return `${sign}${Math.abs(percentChange)}% Bid ${percentChange > 0 ? "Boost" : "Reduction"}: ${startDate}-${endDate}`;
  }

  if (field_name === "manual_bid") {
    const bidValue = formatCurrency(change_value, marketPlace);
    return `${bidValue} Manual Bid: ${startDate}-${endDate}`;
  }

  if (field_name === "timebound_flat_change") {
    const boostValue = formatCurrency(Math.abs(change_value), marketPlace);
    const sign = change_value > 0 ? "+" : "";
    return `${sign}${boostValue} Bid ${change_value > 0 ? "Boost" : "Reduction"}: ${startDate}-${endDate}`;
  }
};

export const TargetBadges = ({ target }) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const { id, manual_bid, ceil_bid, floor_bid, scheduled_actions: scheduledActions, tags } = target;

  const filteredScheduledActions = useMemo(() => {
    return (
      scheduledActions?.filter(
        (sa) =>
          sa.scheduled_action_status !== undefined && ["scheduled", "active"].includes(sa.scheduled_action_status),
      ) ?? []
    );
  }, [scheduledActions]);

  return (
    <>
      {manual_bid ?
        <OverlayTrigger
          overlay={
            <Tooltip placement="top" className="in" id="tooltip-top" bsPrefix="tr-popover-alt bg-purple">
              <span className="text-center mx-auto fs-5">
                Fixed bid manually set to {formatCurrency(manual_bid, marketPlace, false)}
              </span>
            </Tooltip>
          }
        >
          <Badge
            className={`badge bg-trellis-mauve me-2`}
            pill
            style={{
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            <span className="fs-6 ">Fixed Bid</span>
          </Badge>
        </OverlayTrigger>
      : null}
      {ceil_bid || floor_bid ?
        <Badge
          className={`badge bg-trellis-fuchsia me-2`}
          pill
          style={{
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          <span className="fs-6 ">
            Bid Range: {formatCurrency(floor_bid, marketPlace, false)}-{formatCurrency(ceil_bid, marketPlace, false)}
          </span>
        </Badge>
      : null}
      {filteredScheduledActions?.length ?
        filteredScheduledActions?.map((scheduledAction, i) => {
          return (
            <Badge
              key={i}
              className={`badge bg-trellis-fuchsia me-2`}
              pill
              style={{
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              <span className="fs-6 ">{formatScheduledActionLabel(scheduledAction, marketPlace)}</span>
            </Badge>
          );
        })
      : null}
      {tags?.length ?
        <>
          {tags.map((tag) => (
            <Badge
              className="bg-trellis-mauve me-3"
              style={{
                cursor: "pointer",
              }}
              pill
              key={`customTag-${id}-${tag}`}
            >
              {tag}
            </Badge>
          ))}
        </>
      : null}
    </>
  );
};

const Dashboard = () => {
  const { start, end, preStart, preEnd } = useDates();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const acosOrRoas = useAcosOrRoas();

  const tableRef = useRef();

  const [_query, setQuery] = useQueryParams("q", "");
  const [dims, setDims] = useQueryParams("dims", "keyword_text,match_type");
  const [category, setCategory] = useQueryParams("category", null);
  const query = useDebounce(_query, 300);

  const { data: current, isLoading: isCurrentLoading } = useSearchTermReport(
    query,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    1000,
    dims,
    { category },
  );
  const { data: previous, isLoading: isPreviousLoading } = useSearchTermReport(
    query,
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    5000,
    dims,
    { category },
  );

  const data = useMemo(() => {
    if (current) {
      const selectQuery = (d) => d.query;
      const selectKeyword = (d) => `${d?.keyword_text}:${d?.match_type}`;
      const select = dims === "query" ? selectQuery : selectKeyword;
      const previousMap =
        previous?.reduce((acc, row) => {
          acc[select(row)] = row;
          return acc;
        }, {}) ?? {};
      return current.map((row) => {
        const key = select(row);
        row["key"] = key;
        const prevRow = previousMap?.[key];
        const compareFn = (key, row, prevRow) => {
          return {
            [`${key}_delta`]: row[key] - prevRow[key],
            [`${key}_percent`]: prevRow[key] !== 0 ? (row[key] - prevRow[key]) / prevRow[key] : null,
            [`${key}_prev`]: prevRow[key],
          };
        };

        if (prevRow) {
          return {
            ...row,
            ...compareFn("impressions", row, prevRow),
            ...compareFn("clicks", row, prevRow),
            ...compareFn("ad_spend", row, prevRow),
            ...compareFn("cost", row, prevRow),
            ...compareFn("ad_sales", row, prevRow),
            ...compareFn("acos", row, prevRow),
            ...compareFn("roas", row, prevRow),
            ...compareFn("click_thru_rate", row, prevRow),
            ...compareFn("cost_per_click", row, prevRow),
            ...compareFn("conv_rate", row, prevRow),
            ...compareFn("conversion", row, prevRow),
            ...compareFn("viewable_impressions", row, prevRow),
            ...compareFn("viewability_rate", row, prevRow),
            ...compareFn("view_click_through_rate", row, prevRow),
          };
        }
        return row;
      });
    }
    return [];
  }, [current, previous, dims]);

  const columnSpecs = useMemo(() => {
    const perfCol = acosOrRoas({ key: "acos", compareKey: "acos_delta" }, { key: "roas", compareKey: "roas_delta" });
    const cols = [
      { key: "impressions", compareKey: "impressions_percent" },
      { key: "clicks", compareKey: "clicks_percent" },
      { key: "orders", compareKey: "conversion_percent" },
      { key: "ad_spend", compareKey: "ad_spend_percent" },
      { key: "ad_sales", compareKey: "ad_sales_percent" },
      perfCol,
      { key: "cpc", compareKey: "cpc_delta" },
      { key: "conversion_rate", compareKey: "conversion_rate_delta" },
      { key: "ctr", compareKey: "ctr_delta" },
      { key: "viewable_impressions", compareKey: "viewable_impressions_percent" },
      { key: "viewability_rate", compareKey: "viewability_rate_delta" },
      { key: "view_click_through_rate", compareKey: "view_click_through_rate_delta" },
      {
        key: "adv_keyword_cnt",
        options: {
          columnTitle: "Keyword Count",
          format: "number",
          formatter: (v) => v,
        },
      },
      {
        key: "adv_target_cnt",
        options: {
          columnTitle: "Target Count",
          format: "number",
          formatter: (v) => v,
        },
      },
    ];
    if (dims !== "query") {
      cols.unshift({
        key: "match_type",
        options: {
          columnTitle: "Match Type",
          format: "text",
          formatter: (v) => v,
          hideFilter: true,
        },
      });
    }
    return cols;
  }, [acosOrRoas, dims]);

  const tableId = dims === "query" ? "search-term-report-query" : "search-term-report";
  const filterProps = useCustomTableFilters(tableId);

  const hasData = isCurrentLoading || (query && data.length > 0);

  const downloadFilters = {
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    mp: marketPlace.marketPlace,
  };
  if (category) {
    downloadFilters.category_id = category;
  }
  if (query) {
    if (dims === "query") {
      downloadFilters.query = query;
    } else {
      downloadFilters.keyword_text__icontains = query;
    }
  }

  return (
    <Row>
      <Col xs={12}>
        <Row>
          <Col xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: hasData ? "3em" : "10em",
                marginBottom: "1em",
                transition: "all  0.5s",
              }}
            >
              <div style={{ width: "400px" }}>
                <InputGroup className="mb-3">
                  <InputGroup.Text
                    style={{
                      borderTopLeftRadius: "45px",
                      borderBottomLeftRadius: "45px",
                    }}
                  >
                    <i className="fa fa-search" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    onChange={(e) => setQuery(e.target.value)}
                    value={_query}
                  />
                </InputGroup>
              </div>
            </div>
          </Col>
          <Col xs={12} className="fs-standard">
            <Row></Row>
          </Col>
        </Row>
      </Col>
      <Col xs={12} className="ph-5 mb-5 border-bottom fs-standard">
        <Nav
          variant="underline"
          onSelect={(k) => {
            return setDims(k);
          }}
          activeKey={dims}
        >
          <Nav.Item>
            <Nav.Link eventKey="keyword_text,match_type">Keywords</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="query">Queries</Nav.Link>
          </Nav.Item>
        </Nav>
      </Col>
      <Col xs={12} lg={6} xl={2}>
        <CategorySelector
          selectedCategory={category}
          setSelectedCategory={(cat) => {
            setCategory(cat);
          }}
        />
      </Col>
      <Col xs={12} lg={6} xl={8}></Col>
      <Col xs={12} lg={6} xl={2}>
        <DownloadReport url_path="search_term_report" filters={downloadFilters} report_dl="search_terms" />
      </Col>

      <Col xs={12}>
        <Table
          hideSearchFilter
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={tableId}
          isExpandable
          isStatsLoading={isCurrentLoading || isPreviousLoading}
          selectionKey={"key"}
          titleCol={{
            id: "key",
            isStatic: true,
            Header: dims === "query" ? "Query" : "Keyword",
            accessor: "key",
            format: "text",
            style: { width: "300px" },
            width: 300,
            Cell: (props) => {
              return <KeywordCell value={props?.original?.query || props?.original?.keyword_text} columnWidth={300} />;
            },
            Filter: () => null,
          }}
          data={data}
          columnSpecs={columnSpecs}
          getSearchCriteria={(row) => {
            return `${row?.key}`;
          }}
          tableRowDescription={"Search Terms"}
          defaultSorted={[{ id: "impressions", desc: true }]}
          isLoading={isCurrentLoading}
          filterWidget={true}
          hideFilterToggleIcon={true}
          hideResetFiltersIcon={false}
          excludedFilterColumns={dims === "query" ? [] : ["match_type"]}
          {...filterProps}
          additionalFilterOptions={dims === "query" ? [] : [SEARCH_TERM_REPORT_MATCH_TYPE_FILTER]}
          handleBulkChanges={() => {}}
          ExpandedContent={({ row }) => {
            return (
              <ExpanderContainer>
                <QueryExpander
                  row={row}
                  dims={dims}
                  query={row.original.query}
                  keyword_text={row.original.keyword_text}
                  match_type={row.original.match_type}
                  marketPlace={marketPlace}
                  context="search-term-report"
                />
              </ExpanderContainer>
            );
          }}
        />
      </Col>
    </Row>
  );
};

const SearchTermReport = () => {
  const hasOrgSummaryReportModule = useModuleEnabled("ORG_SUMMARY_REPORT");

  useAdvertisementReport();

  const breadCrumbItems = [
    {
      name: "Search Term Report",
      href: "/user/report/search_term_report",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: "Reports",
    },
  ];

  if (
    hasOrgSummaryReportModule &&
    !breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0].dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }

  return (
    <DatesProvider>
      <Grid
        fluid
        style={{
          minHeight: "calc(100vh - 150px)",
          background: "#fefefe",
        }}
      >
        <Row>
          <Col xs={12} className="mb-4">
            <BreadcrumbDates items={breadCrumbItems} />
          </Col>
        </Row>
        <SummaryCard title={`Search Term Report`}>
          <Row
            style={{
              minHeight: "calc(100vh - 350px)",
            }}
          >
            <Col xs={12}>
              <Dashboard />
            </Col>
          </Row>
        </SummaryCard>
      </Grid>
    </DatesProvider>
  );
};

export default SearchTermReport;
